import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDesktop, type AppId } from './DesktopContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { APP_REGISTRY, getAppMeta } from './appRegistry';

const DOCK_APPS = APP_REGISTRY.filter((app) => app.id !== 'publish');

export function Dock() {
  const { openWindow, recenterWindow, windows, activeWindowId } = useDesktop();
  const { metadata } = useCurrentUser();
  const { config } = useAppContext();
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);

  const getAppTitle = (appId: AppId): string => {
    if (appId === 'profile' && metadata?.name) return `${metadata.name}'s Profile`;
    return getAppMeta(appId)?.title ?? 'App';
  };

  const activeAppId = windows.find((w) => w.id === activeWindowId)?.appId;

  const isPublishOpen = windows.some((w) => w.appId === 'publish' && !w.isMinimized);
  const isPublishMinimized = windows.some((w) => w.appId === 'publish' && w.isMinimized);
  const isPublishActive = activeAppId === 'publish' && isPublishOpen;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999]">
      <div className="flex items-end gap-1 px-3 py-2 rounded-2xl bg-background/60 backdrop-blur-xl border border-white/10 shadow-2xl">

        {/* ── Publish (compose) button — visually distinct ── */}
        <button
          className="relative group flex flex-col items-center mr-1"
          onMouseEnter={() => setHoveredApp('publish')}
          onMouseLeave={() => setHoveredApp(null)}
          onClick={() => openWindow('publish', 'Publish')}
          onDoubleClick={() => recenterWindow('publish')}
        >
          {hoveredApp === 'publish' && (
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-popover text-popover-foreground text-xs font-medium whitespace-nowrap shadow-lg border border-border">
              Publish
            </div>
          )}
          <div
            className={cn(
              'relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
              'bg-gradient-to-br from-primary to-primary/70 border border-primary/30',
              'hover:scale-110 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/25',
              isPublishOpen && 'ring-2 ring-primary/70 ring-offset-1 ring-offset-background/50',
              isPublishMinimized && 'opacity-60'
            )}
          >
            <Pencil className="w-5 h-5 text-primary-foreground" />
            {(isPublishOpen || isPublishMinimized) && (
              <div className={cn(
                'absolute -bottom-1 h-1 rounded-full bg-primary-foreground transition-all',
                isPublishActive ? 'w-3' : 'w-1'
              )} />
            )}
          </div>
        </button>

        {/* ── Divider ── */}
        <div className="w-px h-8 bg-white/10 mx-0.5 self-center" />

        {/* ── Regular app icons ── */}
        {DOCK_APPS.filter((app) => app.id === 'settings' || config.dockApps.includes(app.id)).map((app) => {
          const Icon = app.icon;
          const isOpen = windows.some((w) => w.appId === app.id && !w.isMinimized);
          const isMinimized = windows.some((w) => w.appId === app.id && w.isMinimized);
          const isActive = activeAppId === app.id && isOpen;
          const isHovered = hoveredApp === app.id;

          return (
            <button
              key={app.id}
              className="relative group flex flex-col items-center"
              onMouseEnter={() => setHoveredApp(app.id)}
              onMouseLeave={() => setHoveredApp(null)}
              onClick={() => openWindow(app.id, getAppTitle(app.id))}
              onDoubleClick={() => recenterWindow(app.id)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-popover text-popover-foreground text-xs font-medium whitespace-nowrap shadow-lg border border-border">
                  {app.label}
                </div>
              )}

              {/* Icon */}
              <div
                className={cn(
                  'relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
                  'bg-gradient-to-br from-card to-muted border border-white/10',
                  'hover:scale-110 hover:-translate-y-1 hover:shadow-lg',
                  isOpen && 'ring-2 ring-primary/50',
                  isMinimized && 'opacity-60'
                )}
              >
                <Icon className="w-6 h-6 text-foreground" />
                {(isOpen || isMinimized) && (
                  <div className={cn(
                    'absolute -bottom-1 h-1 rounded-full bg-primary transition-all',
                    isActive ? 'w-3' : 'w-1'
                  )} />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
